import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Filter, Calendar, Layers, Banknote, Briefcase, RotateCcw } from 'lucide-react';

const FilterPanel = ({ filters, onFilterChange, onResetFilters, filterOptions }) => {
  return (
    <Card className="p-6 mb-6 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 shadow-xl hover:shadow-2xl transition-all duration-300">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-lg">
              <Filter className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Project Filters</h2>
              <p className="text-slate-600 font-medium">Customize your dashboard view</p>
            </div>
          </div>
          
          {/* Reset Button - Made smaller and more attractive */}
          <Button 
            onClick={onResetFilters}
            size="sm"
            variant="outline" 
            className="border-2 border-slate-300 hover:border-teal-400 hover:bg-teal-50 text-slate-700 font-medium transition-all duration-300 px-4 py-2 text-sm"
          >
            <RotateCcw className="h-3 w-3 mr-2" />
            Reset
          </Button>
        </div>
        
        {/* Filter Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Calendar className="h-3 w-3 text-teal-600" />
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Financial Year</label>
            </div>
            <Select value={filters.financialYear} onValueChange={(value) => onFilterChange('financialYear', value)}>
              <SelectTrigger className="bg-white border-2 border-slate-300 hover:border-teal-400 focus:border-teal-500 transition-colors shadow-sm h-12 text-slate-700 font-medium">
                <SelectValue>
                  {filters.financialYear === 'all' ? 'All Financial Years' : filters.financialYear}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="border-2 border-slate-200">
                <SelectItem value="all" className="font-medium">All Financial Years</SelectItem>
                {filterOptions.financialYears?.map(year => (
                  <SelectItem key={year} value={year} className="font-medium">{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Layers className="h-3 w-3 text-blue-600" />
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Fund Category</label>
            </div>
            <Select value={filters.fundCategory} onValueChange={(value) => onFilterChange('fundCategory', value)}>
              <SelectTrigger className="bg-white border-2 border-slate-300 hover:border-blue-400 focus:border-blue-500 transition-colors shadow-sm h-12 text-slate-700 font-medium">
                <SelectValue placeholder="Select Fund Category">
                  {filters.fundCategory === 'all' ? 'All Categories' : 
                   (() => {
                     const selectedCategory = filterOptions.fundCategories?.find(cat => 
                       cat.id == filters.fundCategory
                     );
                     return selectedCategory ? selectedCategory.name : filters.fundCategory;
                   })()
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="border-2 border-slate-200">
                <SelectItem value="all" className="font-medium">All Categories</SelectItem>
                {filterOptions.fundCategories?.map(category => (
                  <SelectItem key={category.id || category.value} value={category.id || category.value} className="font-medium">
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Banknote className="h-3 w-3 text-emerald-600" />
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Fund Head</label>
            </div>
            <Select value={filters.fundHead} onValueChange={(value) => onFilterChange('fundHead', value)}>
              <SelectTrigger className="bg-white border-2 border-slate-300 hover:border-emerald-400 focus:border-emerald-500 transition-colors shadow-sm h-12 text-slate-700 font-medium">
                <SelectValue placeholder="Select Fund Head">
                  {filters.fundHead === 'all' ? 'All Fund Heads' : 
                   (() => {
                     const selectedHead = filterOptions.fundHeads?.find(head => head.id == filters.fundHead);
                     return selectedHead ? selectedHead.name : filters.fundHead;
                   })()
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="border-2 border-slate-200">
                <SelectItem value="all" className="font-medium">All Fund Heads</SelectItem>
                {filterOptions.fundHeads?.map(head => (
                  <SelectItem key={head.id} value={head.id} className="font-medium">
                    {head.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Project Stage section removed as per new requirements */}
        </div>
      </div>
    </Card>
  );
};

export default FilterPanel;